
import { Component, Vue, Prop } from 'vue-property-decorator';

type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning';
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

@Component
export default class ActionButton extends Vue {
  @Prop({ default: 'button' }) readonly type!: 'button' | 'submit' | 'reset';
  @Prop({ default: 'primary' }) readonly variant!: ButtonVariant;
  @Prop({ default: 'md' }) readonly size!: ButtonSize;
  @Prop({ default: false }) readonly loading!: boolean;
  @Prop({ default: false }) readonly disabled!: boolean;
  @Prop({ default: false }) readonly block!: boolean;
  @Prop({ default: '#40b99d' }) readonly color!: string;

  get buttonClasses(): string {
    const baseClasses = [
      'inline-flex items-center justify-center font-semibold rounded-xl',
      'transition-all duration-200 focus:outline-none focus:ring-4',
      'disabled:cursor-not-allowed transform hover:-translate-y-0.5',
      'active:translate-y-0 shadow-lg hover:shadow-xl',
    ];

    // Size classes
    const sizeClasses = {
      sm: 'px-4 py-2 text-sm',
      md: 'px-6 py-3 text-base',
      lg: 'px-8 py-4 text-lg',
      xl: 'px-10 py-5 text-xl',
    };
    baseClasses.push(sizeClasses[this.size]);

    // Variant classes
    if (this.variant === 'primary') {
      if (this.disabled || this.loading) {
        baseClasses.push('bg-gray-400 text-white cursor-not-allowed transform-none');
      } else {
        baseClasses.push(
          'text-white hover:opacity-90 focus:ring-offset-2',
          'shadow-lg hover:shadow-xl'
        );
      }
    } else {
      const variantClasses = {
        secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500/20',
        success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500/20',
        danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500/20',
        warning: 'bg-amber-600 text-white hover:bg-amber-700 focus:ring-amber-500/20',
      };
      baseClasses.push(variantClasses[this.variant] || variantClasses.secondary);
    }

    // Block class
    if (this.block) {
      baseClasses.push('w-full');
    }

    return baseClasses.join(' ');
  }

  get dynamicStyles(): Record<string, string> {
    if (this.variant === 'primary' && !this.disabled && !this.loading) {
      return {
        backgroundColor: this.color,
        '--tw-ring-color': `${this.color}33`, // 20% opacity
      };
    }
    return {};
  }

  handleClick(event: Event): void {
    if (!this.disabled && !this.loading) {
      this.$emit('click', event);
    }
  }
}
